import React from "react";
import { Skeleton, TextField } from "@mui/material";
import { PickersCalendarHeader } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import { Controller, FieldValues, Path, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { calendarLocalization } from "../../../../../../components/common/utils/calendar";
// Required for localization.
import "dayjs/locale/es";
import "dayjs/locale/it";
import "dayjs/locale/en";

interface Props<T extends FieldValues> {
	form: UseFormReturn<T>;
	isLoading: boolean;
	name: Path<T>;
	valueStyle?: "long" | "short";
	fullWidth?: boolean;
}

const CustomCalendarHeader = (props: any) => <PickersCalendarHeader {...props} format={"MMMM"} />;

// Fixed year used internally — hidden from user
const INTERNAL_YEAR = 2002;

// Convert MM-DD string to a dayjs date with internal fixed year
const parseToDayjs = (mmdd: string): Dayjs => dayjs(`${INTERNAL_YEAR}-${mmdd}`, "YYYY-MM-DD");

// Convert dayjs date to MM-DD string
const formatToMMDD = (date: Dayjs): string => date.format("MM-DD");

/*This component is exclusively used for company detail component
FiscalPeriodPicker component allows selection of a fiscal period start date.
It integrates with React Hook Form for form state management.
Displays a date picker restricted to month and day, formatting the value as "MM-DD/MM-DD".
Shows a loading skeleton when isLoading is true.
*/
const FiscalPeriodPicker = <T extends FieldValues>({
	form,
	isLoading,
	name,
	valueStyle = "long",
	fullWidth,
}: Props<T>): React.JSX.Element => {
	const { i18n } = useTranslation();
	const localization = calendarLocalization(i18n.language);
	dayjs.locale(i18n.language);
	const { t } = useTranslation("feature.company");

	/*endDate is used to obtain the end fiscal period from the start fiscal date,
	  it is calculated by adding 1 to the current year and substract 1 day */
	const fiscalPeriodValue = form.watch(name);
	const endDate = fiscalPeriodValue ? parseToDayjs(fiscalPeriodValue.split("/")[1]) : undefined;

	if (isLoading) {
		return <Skeleton height={"40px"} variant="rounded" className="loading-field" />;
	}

	const dateFormat = t("fiscal period format", { context: valueStyle });
	const minDate = dayjs(`${INTERNAL_YEAR}-01-01`);
	const maxDate = dayjs(`${INTERNAL_YEAR}-12-31`);

	return (
		<>
			<LocalizationProvider dateAdapter={AdapterDayjs} localeText={localization}>
				<Controller
					name={name}
					control={form.control}
					render={({ field }) => {
						const startDateStr = field.value?.split("/")[0];
						const dateValue = startDateStr ? parseToDayjs(startDateStr) : null;
						return (
							<DatePicker
								value={dateValue}
								views={["month", "day"]}
								minDate={minDate}
								maxDate={maxDate}
								slotProps={{
									textField: {
										fullWidth: fullWidth,
									},
								}}
								slots={{
									calendarHeader: CustomCalendarHeader,
									field: (params) => (
										<TextField
											{...params}
											fullWidth={fullWidth}
											label={t("fiscal year")}
											size="small"
											value={
												dateValue
													? t("from {{start}} until {{end}} of the next year", {
															context: valueStyle,
															start: dateValue.format(dateFormat),
															end: endDate?.format(dateFormat) ?? "",
														})
													: t("select fiscal period start")
											}
											/* @todo when we will update MUI to version 7 */
											/* InputLabelProps is deprecated, for this version of MUI this is the correct way to set it */
											InputProps={{
												...params.InputProps,
												inputProps: {
													...params.inputProps,
													onKeyDown: (e) => e.preventDefault(),
												},
											}}
										/>
									),
								}}
								onChange={(date) => {
									if (date?.isValid()) {
										const start = formatToMMDD(date);
										const end = formatToMMDD(date.add(1, "year").subtract(1, "day"));
										field.onChange(`${start}/${end}`);
									}
								}}
							/>
						);
					}}
				/>
			</LocalizationProvider>
		</>
	);
};

export default FiscalPeriodPicker;
